/**
 * Shared frame for Ross's analysis cards (RossDecisionCard,
 * RossNotImplementedCard): the Ross image, the "✓ Mark as Reviewed" button
 * with the analysis date, and the processing / reviewed states. Marking as
 * reviewed dismisses the decision ("Dismiss", decision_type "ross_analysis");
 * only store owners can do it. The card's own content goes in `children`.
 */

import { Box, Button, Typography } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCanRespondToDecisions from "../../../../../hooks/useCanRespondToDecisions";
import {
  clearRuleEngineResponse,
  respondToRuleEngineDecision,
  storeAnalyticsSelector,
} from "../../../../../redux/slices/store_analytics";
import { trackEvent } from "../../../../../utils/analytics";

// Helper to format date to display format
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Calculate relative time string
const getRelativeTime = (dateString) => {
  if (!dateString) return "";
  const analysisDateObj = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  analysisDateObj.setHours(0, 0, 0, 0);

  const diffTime = today - analysisDateObj;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Analyzed Today";
  if (diffDays === 1) return "Analyzed Yesterday";
  if (diffDays > 1 && diffDays <= 4) return `Analyzed ${diffDays} days ago`;
  return `Analyzed ${formatDate(dateString)}`;
};

const RossReviewable = ({
  asin,
  sku,
  executionId,
  store,
  marketplace,
  rossData,
  onRemove,
  sx,
  children,
}) => {
  const dispatch = useDispatch();
  const {
    saveRuleEngineResponseLoading: saveRuleEngineResponseLoadingMap,
    saveRuleEngineResponseStatus,
  } = useSelector(storeAnalyticsSelector);
  const agentName = (rossData?.decision?.agent || "ross").toLowerCase();
  const statusKey = `${executionId}_${asin}_${agentName}`;
  const saveRuleEngineResponseLoading =
    !!saveRuleEngineResponseLoadingMap[statusKey];
  const [isReviewed, setIsReviewed] = useState(false);

  // Extract date from rossData.created_at (format: "2026-02-09T12:34:56")
  const analysisDate = rossData?.created_at?.split("T")[0] || "";
  const relativeTimeText = getRelativeTime(analysisDate);

  const userAllowedToRespond = useCanRespondToDecisions(store);

  const handleMarkAsReviewed = async () => {
    trackEvent("ross_marked_as_reviewed", {
      asin,
      store,
      execution_id: executionId,
    });
    const payload = {
      asin: asin,
      sku: sku,
      store: store,
      execution_id: executionId,
      status_key: statusKey,
      marketplace: marketplace,
      user_response: "Dismiss",
      decision_type: "ross_analysis",
    };

    const result = await dispatch(respondToRuleEngineDecision(payload));
    if (result.success) {
      setIsReviewed(true);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        position: "relative",
        minHeight: "150px",
        minWidth: "380px",
        width: "100%",
        ...sx,
      }}
    >
      {/* ROSS Agent Image */}
      <img
        src="/assets/ross.png"
        alt="Ross"
        style={{
          position: "absolute",
          bottom: -20,
          right: -31,
          width: 100,
          height: 100,
          opacity: 0.7,
        }}
      />

      {saveRuleEngineResponseLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
          <div
            style={{
              width: 16,
              height: 16,
              border: "2px solid #e3f2fd",
              borderTop: "2px solid #1976d2",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <Typography variant="body2" sx={{ color: "#1976d2" }}>
            Processing...
          </Typography>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </Box>
      ) : isReviewed || saveRuleEngineResponseStatus[statusKey]?.message ? (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "#4caf50",
              fontWeight: 500,
              py: 1,
            }}
          >
            {saveRuleEngineResponseStatus[statusKey]?.message ||
              "Analysis marked as reviewed"}
          </Typography>
          {onRemove && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                trackEvent("ross_response_cleared", {
                  asin,
                  store,
                  execution_id: executionId,
                });
                dispatch(
                  clearRuleEngineResponse({
                    execution_id: executionId,
                    status_key: statusKey,
                  })
                );
                onRemove();
              }}
              sx={{
                mt: 1,
                color: "#666",
                borderColor: "#e0e0e0",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 3,
                "&:hover": {
                  borderColor: "#999",
                  backgroundColor: "transparent",
                },
              }}
            >
              Close
            </Button>
          )}
        </Box>
      ) : (
        <>
          {children}

          {/* Mark as Reviewed Button and Analysis Date */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pr: 10, // Add padding to avoid overlap with ROSS image
              mt: 2, // Add top margin for spacing after Iris suggestion box
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={
                  !userAllowedToRespond || saveRuleEngineResponseLoading
                }
                onClick={
                  userAllowedToRespond ? handleMarkAsReviewed : undefined
                }
                sx={{
                  backgroundColor: userAllowedToRespond ? "#fff" : "#f5f5f5",
                  color: userAllowedToRespond ? "#1976d2" : "#9e9e9e",
                  borderColor: userAllowedToRespond ? "#1976d2" : "#e0e0e0",
                  textTransform: "none",
                  fontWeight: 500,
                  boxShadow: "none",
                  borderRadius: 2,
                  px: 2,
                  "&:hover": userAllowedToRespond
                    ? {
                        backgroundColor: "#e3f2fd",
                        borderColor: "#1565c0",
                      }
                    : {},
                }}
              >
                ✓ Mark as Reviewed
              </Button>
              <Typography
                variant="caption"
                sx={{
                  color: "#999",
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                }}
              >
                ({relativeTimeText})
              </Typography>
            </Box>
            <Box />
          </Box>

          {!userAllowedToRespond && (
            <Typography
              variant="body2"
              sx={{
                color: "red",
                fontStyle: "italic",
                mt: 1,
                textAlign: "center",
                fontSize: "0.75rem",
              }}
            >
              Only the store owner can mark as reviewed.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default RossReviewable;
